import { WithActionBar } from "@/src/components/wrappers/WithActionBar";
import { Content } from "@/src/components/wrappers/Content";
import { Steps } from "@/src/components/ui/steps";
import { useFetch } from "@/src/lib/useFetch";
import { useToast } from "@/src/components/ui/use-toast";
import { useLoaderData, useLocation, useNavigate } from "react-router-dom";
import { useStepsStore, BUILDER_STEPS, BUILDER_STEP_LABELS } from "./EditOffer/state";
import { EditOfferForm } from "./EditOffer/EditOfferForm";
import { OfferForm } from "./EditOffer/offer.schema";
import { OfferActionBar } from "./OfferActionBar";
import { Enquiry, Offer } from "./model";
import { useEffect, useState } from "react";
import { useOffersStore } from "../../stores/offersStore";
import { Button } from "../../components/ui/button";
import { MailSearchIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { useOfferData } from "@/src/hooks/useOfferData";
import { parseDateIntoTime } from "./EditOffer/ChangeYachtDetailsModal/ChangeYachtDetailsModal";
import { logOfferCreatorAction } from "./offerCreatorLogs";

export function AddOffer() {
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [enquiry, setEnquiry] = useState<Enquiry | null>(null);
  const offerLoaded = useLoaderData() as Offer;
  const location = useLocation();
  const url = location.pathname;
  const offerId = url.includes("/hubspot/") || url.includes("/add") ? undefined : location.pathname.split("/").pop();
  const { offerData, usersData, yachtsData, feedbacksData, isLoadingYachts } = useOfferData(offerId);

  let offer: Offer;
  const { toast } = useToast();

  if (!url.includes("/hubspot/")) {
    offer = { ...offerData, ...usersData, ...yachtsData, ...feedbacksData } as Offer;
  } else {
    offer = offerLoaded;
  }

  const { fetch, isFetching } = useFetch();
  const navigate = useNavigate();
  const steps = useStepsStore();
  const { setOfferYachts, yachtsFilters, setYachtsFilters } = useOffersStore();

  const fetchEnquiry = () =>
    fetch<Enquiry>("/api/enquiries/" + offer.hubspotId).then((r) => {
      setEnquiry(r);
    });

  useEffect(() => {
    if (offer?.customer === null) {
      console.log(offer?.customer);
      setTimeout(() => {
        toast({
          title: "Customer not found",
          description: "Could not fetch customer data for this HubSpot deal",
          variant: "destructive",
        });
      }, 1000);
    }
  }, [offer?.customer]);

  useEffect(() => {
    // remove the url from ls after successful redirect
    localStorage.removeItem("redirectUrl");
    steps.setStep("Customer");
    setOfferYachts(
      offer.yachts?.map((y) => ({
        ...y,
        ignoreAvailability: y.ignoreAvailability ?? false,
        startTime: parseDateIntoTime(y.startDate),
        endTime: parseDateIntoTime(y.endDate),
      })) || [],
    );
    logOfferCreatorAction({
      eventType: "offer.create_loaded",
      meta: {
        source: url.includes("/hubspot/") ? "hubspot" : "blank",
        hubspot_id: offer?.hubspotId,
        initial_yacht_count: offer?.yachts?.length ?? 0,
      },
    });
  }, []);

  useEffect(() => {
    if (!!offerLoaded?.guests && +offerLoaded?.guests > 1) {
      setYachtsFilters({ ...yachtsFilters, people: +offerLoaded.guests });
    }
  }, []);

  const openEnquiryModal = () => {
    fetchEnquiry();
    setShowEnquiryModal(true);
  };

  const parseDateAndTime = (date: string, time: string) => {
    const [hours, minutes] = time.split(":").map((num) => parseInt(num, 10));
    const dt = new Date(date);
    dt.setUTCHours(hours);
    dt.setUTCMinutes(minutes);
    return dt.toISOString();
  };

  const onSubmit = async (data: OfferForm, emails: string[]) => {
    if (!isFetching) {
      try {
        logOfferCreatorAction({
          eventType: "offer.save_intent",
          meta: {
            mode: "create",
            status_after: "saved",
            selected_yacht_count: data.yachts?.length ?? 0,
            email_recipient_count: emails.length,
          },
        });
        const response = await fetch<Offer>("/api/offers", "POST", {
          title: data?.description?.title,
          intro: data?.description?.intro,
          brand: data?.description?.brand,
          currency: data?.description?.currency,
          link: "",
          dealLink: "",
          status: "saved",
          charterStartDate: enquiry?.checkIn || null,
          charterEndDate: enquiry?.checkOut || null,
          charterType: data?.description?.charterType || "skippered",
          customer: data?.customer,
          hubspotId: data?.hubspotId,
          createDeal: data?.customer.createDeal,
          broker: data?.description?.broker?.id,
          guests: data?.description?.guests,
          yachts: data?.yachts?.map((yt) => ({
            startLocation: yt.startLocation.id,
            endLocation: yt.endLocation.id,
            yacht: yt.yacht.id,
            paymentSteps: yt.paymentSteps,
            yachtOnHold: yt.yachtOnHold ?? false,
            ignoreAvailability: yt.ignoreAvailability ?? false,
            holdExpiresAt: yt.holdExpiresAt ?? null,
            brokerNote: yt.brokerNote ?? null,
            startDate: parseDateAndTime(yt.startDate, yt.startTime),
            endDate: parseDateAndTime(yt.endDate, yt.endTime),
            discountCharterPrice: yt.discountCharterPrice ?? 0,
            cardPayments: yt.cardPayments,
            amenitiesDetails: yt.amenitiesDetails,
            pricing: {
              ...yt.pricing,
              yachtCurrencyCode: yt.pricing.yachtCurrencyCode ?? null,
            },
            order: yt.order,
          })),
          customAmenities: data?.yachts?.map((yt) => yt.customAmenities ?? [])?.flat(),
          mailingSettings: data?.mailing,
        });

        await fetch(`/api/offers/${response.id}/email`, "POST", emails);
        toast({
          title: "Offer sent",
          description: "The offer has been sent",
          variant: "default",
        });
        return response.id;

        // navigate(`/offers`);
      } catch (e: any) {
        console.error(e);
        toast({
          title: "Failed to save offer",
          description: e.message || "An error occurred while saving the offer",
          variant: "destructive",
        });
      }
    }
  };

  const onDraft = async (data: OfferForm) => {
    if (!isFetching) {
      try {
        logOfferCreatorAction({
          eventType: "offer.save_draft_intent",
          meta: {
            mode: "create",
            status_after: "draft",
            selected_yacht_count: data.yachts?.length ?? 0,
          },
        });
        await fetch<Offer>("/api/offers", "POST", {
          title: data?.description?.title,
          intro: data?.description?.intro,
          brand: data?.description?.brand,
          currency: data?.description?.currency,
          link: "",
          dealLink: "",
          charterStartDate: enquiry?.checkIn || null,
          charterEndDate: enquiry?.checkOut || null,
          charterType: data?.description?.charterType || "skippered",
          status: "draft",
          customer: data?.customer,
          hubspotId: data?.hubspotId,
          createDeal: data?.customer.createDeal,
          broker: data?.description?.broker?.id,
          guests: data?.description?.guests,
          yachts: data?.yachts?.map((yt) => ({
            startLocation: yt.startLocation.id,
            endLocation: yt.endLocation.id,
            yacht: yt.yacht.id,
            paymentSteps: yt.paymentSteps,
            yachtOnHold: yt.yachtOnHold ?? false,
            ignoreAvailability: yt.ignoreAvailability ?? false,
            holdExpiresAt: yt.holdExpiresAt ?? null,
            brokerNote: yt.brokerNote ?? null,
            startDate: parseDateAndTime(yt.startDate, yt.startTime),
            endDate: parseDateAndTime(yt.endDate, yt.endTime),
            discountCharterPrice: yt.discountCharterPrice ?? 0,
            cardPayments: true,
            amenitiesDetails: yt.amenitiesDetails,
            pricing: yt.pricing,
            order: yt.order,
          })),
          customAmenities: data?.yachts?.map((yt) => yt.customAmenities ?? [])?.flat(),
          mailingSettings: data?.mailing,
        });
        setOfferYachts([]);
        toast({
          title: "Offer saved",
          description: "The offer has been drafted",
          variant: "default",
        });
        navigate(`/offers`);
      } catch (e: any) {
        console.error(e);
        toast({
          title: "Failed to save draft offer",
          description: e.message || "An error occurred while saving the draft",
          variant: "destructive",
        });
      }
    }
  };

  // Save the draft, then open the saved offer straight on the Preview step so
  // the broker can see the real client page (needs a persisted offer id).
  const onSaveForPreview = async (data: OfferForm) => {
    if (isFetching) return;
    try {
      const response = await fetch<Offer>("/api/offers", "POST", {
        title: data?.description?.title,
        intro: data?.description?.intro,
        brand: data?.description?.brand,
        currency: data?.description?.currency,
        link: "",
        dealLink: "",
        charterStartDate: enquiry?.checkIn || null,
        charterEndDate: enquiry?.checkOut || null,
        charterType: data?.description?.charterType || "skippered",
        status: "draft",
        customer: data?.customer,
        hubspotId: data?.hubspotId,
        createDeal: data?.customer.createDeal,
        broker: data?.description?.broker?.id,
        guests: data?.description?.guests,
        yachts: data?.yachts?.map((yt) => ({
          startLocation: yt.startLocation.id,
          endLocation: yt.endLocation.id,
          yacht: yt.yacht.id,
          paymentSteps: yt.paymentSteps,
          yachtOnHold: yt.yachtOnHold ?? false,
          ignoreAvailability: yt.ignoreAvailability ?? false,
          holdExpiresAt: yt.holdExpiresAt ?? null,
          brokerNote: yt.brokerNote ?? null,
          startDate: parseDateAndTime(yt.startDate, yt.startTime),
          endDate: parseDateAndTime(yt.endDate, yt.endTime),
          discountCharterPrice: yt.discountCharterPrice ?? 0,
          cardPayments: true,
          amenitiesDetails: yt.amenitiesDetails,
          pricing: yt.pricing,
          order: yt.order,
        })),
        customAmenities: data?.yachts?.map((yt) => yt.customAmenities ?? [])?.flat(),
        mailingSettings: data?.mailing,
      });
      setOfferYachts([]);
      toast({ title: "Draft saved", description: "Opening the client preview…", variant: "default" });
      navigate(`/offers/edit/${response.id}?step=Preview`);
    } catch (e: any) {
      console.error(e);
      toast({
        title: "Couldn't save for preview",
        description: e.message || "An error occurred while saving the draft",
        variant: "destructive",
      });
    }
  };

  return (
    <WithActionBar actionBar={<OfferActionBar />}>
      <Content
        title={"Offer details"}
        contentAction={
          offer?.hubspotId && (
            <Dialog onOpenChange={setShowEnquiryModal} open={showEnquiryModal}>
              <DialogTrigger asChild>
                <Button onClick={openEnquiryModal} className="gap-2">
                  <MailSearchIcon size={16} />
                  Show Enquiry details
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-[720px] w-full">
                <DialogHeader>
                  <DialogTitle className="text-2xl">Enquiry details</DialogTitle>
                  {enquiry ? <EnquiryModalContent {...enquiry} /> : <p>No enquiry related to that deal.</p>}
                </DialogHeader>
                <DialogFooter>
                  <Button onClick={() => setShowEnquiryModal(false)} className="self-end" variant="default">
                    Close
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      >
        <Steps onStepChange={steps.setStep} step={steps.step as (typeof BUILDER_STEPS)[number]} items={BUILDER_STEPS} labels={BUILDER_STEP_LABELS} className="mb-6" />
        <EditOfferForm offer={offer} onDraft={onDraft} onSubmit={onSubmit} onSaveForPreview={onSaveForPreview} />
      </Content>
    </WithActionBar>
  );
}

const EnquiryModalContent = ({
  country,
  cabinsMin,
  cabinsMax,
  title,
  budget,
  charterType,
  checkIn,
  checkOut,
  guests,
  yachtType,
}: Enquiry) => {
  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="flex flex-col">
        <h4 className="text-xl font-semibold mb-3">Start/end dates & Location</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col">
            <span className="text-slate-600">Booking date</span>
            <span className="font-semibold">
              {new Date(checkIn!).toLocaleDateString("en-EN", {
                year: "numeric",
                month: "short",
                day: "2-digit",
              })}{" "}
              -{" "}
              {new Date(checkOut!).toLocaleDateString("en-EN", {
                year: "numeric",
                month: "short",
                day: "2-digit",
              })}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-600">Location</span>
            <span className="font-semibold">{country}</span>
          </div>
        </div>
      </div>
      <Separator />
      <div className="flex flex-col">
        <h4 className="text-xl font-semibold mb-3">Charter type</h4>
        <span className="font-semibold">{charterType}</span>
      </div>
      <Separator />
      <div className="flex flex-col">
        <h4 className="text-xl font-semibold mb-3">Basic details</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col">
            <span className="text-slate-600">Cabins</span>
            <span className="font-semibold">
              {cabinsMin} - {cabinsMax}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-600">Number of guests</span>
            <span className="font-semibold">{guests}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-600">Rough budget</span>
            <span className="font-semibold">{budget}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-slate-600">Yacht types</span>
            <span className="font-semibold">{yachtType?.map((t, i) => (i > 0 ? `, ${t}` : t))}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
